import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  HomeIcon,
  BriefcaseIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import clientService from '../../services/clientService';
import accountService from '../../services/accountService';
import loanService from '../../services/loanService';
import { useToast } from '../../contexts/ToastContext';
import { format } from 'date-fns';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();
  
  const [client, setClient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchClientData();
  }, [id]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      const [clientData, accountsData, loansData] = await Promise.all([
        clientService.getClient(id),
        accountService.getAllAccounts({ client_id: id }),
        loanService.getAllLoans({ client_id: id })
      ]);
     // console.log(loansData);
      setClient(clientData);
      setAccounts(accountsData.accounts || []);
      setLoans(loansData.loans);
    } catch (error) {
      showError('Erreur lors du chargement des données du client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: { label: 'Actif', color: 'badge-success' },
      inactive: { label: 'Inactif', color: 'badge-warning' },
      suspended: { label: 'Suspendu', color: 'badge-danger' },
      pending: { label: 'En attente', color: 'badge-info' },
    };
    const config = configs[status] || { label: status, color: 'badge-info' };
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des données du client...</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Client non trouvé</h3>
        <p className="mt-1 text-sm text-gray-500">Le client que vous recherchez n'existe pas.</p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/clients')}
            className="btn-primary"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Détails du Client</h1>
          <p className="mt-1 text-sm text-gray-600">
            Informations complètes et historique
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/clients/edit/${id}`)}
            className="btn-secondary"
          >
            Modifier
          </button>
          <button
            onClick={() => navigate('/clients')}
            className="btn-secondary"
          >
            Retour
          </button>
        </div>
      </div>

      {/* Client Info Card */}
      <div className="card">
        <div className="p-6">
          <div className="flex items-start space-x-6">
            {/* Client Photo/Avatar */}
            <div className="flex-shrink-0">
              <div className="h-24 w-24 rounded-full bg-primary-100 flex items-center justify-center">
                <UserCircleIcon className="h-20 w-20 text-primary-600" />
              </div>
            </div>

            {/* Client Info */}
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {client.first_name} {client.last_name}
                  </h2>
                  <div className="flex items-center mt-1 space-x-4">
                    <span className="text-sm text-gray-600">{client.client_number}</span>
                    {getStatusBadge(client.status)}
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="flex items-center">
                  <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-sm text-gray-900">{client.phone || 'Non renseigné'}</span>
                </div>
                <div className="flex items-center">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-sm text-gray-900">{client.email || 'Non renseigné'}</span>
                </div>
                <div className="flex items-center">
                  <HomeIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-sm text-gray-900">{client.address || 'Non renseigné'}</span>
                </div>
                <div className="flex items-center">
                  <BriefcaseIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-sm text-gray-900">{client.occupation || 'Non renseigné'}</span>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-sm text-gray-900">
                    {client.date_of_birth ? `${format(new Date(client.date_of_birth), 'dd/MM/yyyy')} (${calculateAge(client.date_of_birth)} ans)` : 'Non renseigné'}
                  </span>
                </div>
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-sm text-gray-900">
                    {client.monthly_income ? `${client.monthly_income.toLocaleString('fr-FR')} HTG/mois` : 'Non renseigné'}
                  </span>
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">ID National</h4>
                    <p className="mt-1 text-sm text-gray-900">{client.national_id || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Genre</h4>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{client.gender || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Date d'inscription</h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {client.created_at ? format(new Date(client.created_at), 'dd/MM/yyyy') : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Aperçu
          </button>
          <button
            onClick={() => setActiveTab('accounts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'accounts'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Comptes ({accounts.length})
          </button>
          <button
            onClick={() => setActiveTab('loans')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'loans'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Prêts ({loans.length})
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'documents'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Documents
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Accounts Summary */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  <CreditCardIcon className="h-5 w-5 inline-block mr-2" />
                  Comptes d'Épargne
                </h3>
                {accounts.length > 0 ? (
                  <div className="space-y-4">
                    {accounts.map((account) => (
                      <div key={account.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{account.account_number}</p>
                          <p className="text-xs text-gray-500 capitalize">{account.account_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {account.balance.toLocaleString('fr-FR')} HTG
                          </p>
                          <p className="text-xs text-gray-500">Taux: {account.interest_rate}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucun compte d'épargne</p>
                )}
              </div>
            </div>

            {/* Loans Summary */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  <CurrencyDollarIcon className="h-5 w-5 inline-block mr-2" />
                  Prêts en Cours
                </h3>
                {loans.length > 0 ? (
                  <div className="space-y-4">
                    {loans.map((loan) => (
                      <div key={loan.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{loan.loan_number}</p>
                            <p className="text-xs text-gray-500">Montant: {loan.amount.toLocaleString('fr-FR')} HTG</p>
                          </div>
                          <span className={`badge ${
                            loan.status === 'active' ? 'badge-success' :
                            loan.status === 'pending' ? 'badge-warning' :
                            loan.status === 'defaulted' ? 'badge-danger' : 'badge-info'
                          }`}>
                            {loan.status}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Début: {loan.start_date ? format(new Date(loan.start_date), 'dd/MM/yyyy') : '-'} • 
                          Durée: {loan.term} mois • 
                          Reste: {(loan.amount_due || 0).toLocaleString('fr-FR')} HTG
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucun prêt en cours</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'accounts' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Comptes d'Épargne</h3>
              {accounts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          N° Compte
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Solde
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Taux d'intérêt
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date d'ouverture
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {accounts.map((account) => (
                        <tr key={account.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {account.account_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                            {account.account_type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {account.balance.toLocaleString('fr-FR')} HTG
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {account.interest_rate}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {account.opened_date ? format(new Date(account.opened_date), 'dd/MM/yyyy') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(account.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => navigate(`/accounts/${account.id}`)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              Voir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun compte d'épargne</h3>
                  <p className="mt-1 text-sm text-gray-500">Ce client n'a pas encore de compte d'épargne.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'loans' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Historique des Prêts</h3>
              {loans.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          N° Prêt
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Durée
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date Début
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date Fin
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reste dû
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loans.map((loan) => (
                        <tr key={loan.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {loan.loan_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {loan.amount.toLocaleString('fr-FR')} HTG
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {loan.term} mois
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {loan.start_date ? format(new Date(loan.start_date), 'dd/MM/yyyy') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {loan.end_date ? format(new Date(loan.end_date), 'dd/MM/yyyy') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {(loan.amount_due || 0).toLocaleString('fr-FR')} HTG
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(loan.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => navigate(`/loans/${loan.id}`)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              Voir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun prêt</h3>
                  <p className="mt-1 text-sm text-gray-500">Ce client n'a pas encore de prêt.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Document Upload Card */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h4 className="mt-2 text-sm font-medium text-gray-900">Ajouter un document</h4>
                  <p className="mt-1 text-xs text-gray-500">Téléchargez des documents pour ce client</p>
                  <button className="mt-4 btn-secondary text-sm">
                    Télécharger
                  </button>
                </div>

                {/* Document List */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Pièce d'identité</p>
                        <p className="text-xs text-gray-500">CNI ou Passeport</p>
                      </div>
                    </div>
                    <button className="text-sm text-primary-600 hover:text-primary-900">
                      Télécharger
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Justificatif de domicile</p>
                        <p className="text-xs text-gray-500">Facture récente</p>
                      </div>
                    </div>
                    <button className="text-sm text-primary-600 hover:text-primary-900">
                      Télécharger
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;